import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Avatar,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth, ROLE_LABELS } from '../AuthContext';
import { useThemeMode } from '../ThemeModeContext';

const NOTIFICATIONS_POLL_MS = 20000;

const Navbar = () => {
  const { currentUser, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);

  useEffect(() => {
    if (!currentUser?.id) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, NOTIFICATIONS_POLL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const fetchNotifications = async () => {
    const { data, error } = await supabase
      .from('notifications')
      .select('id, title, body, link, is_read, created_at')
      .eq('user_id', currentUser.id)
      .order('created_at', { ascending: false })
      .limit(20);
    if (error) {
      console.error('Error fetching notifications:', error);
      return;
    }
    setNotifications(data || []);
    setUnreadCount((data || []).filter((n) => !n.is_read).length);
  };

  const openNotifMenu = async (e) => {
    setNotifAnchorEl(e.currentTarget);
    if (unreadCount > 0) {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', currentUser.id)
        .eq('is_read', false);
      if (error) console.error('Error marking notifications as read:', error);
      else {
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      }
    }
  };

  const closeNotifMenu = () => setNotifAnchorEl(null);

  const handleNotifClick = (notif) => {
    closeNotifMenu();
    if (notif.link) navigate(notif.link);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexGrow: 1,
            py: 0.5,
            minWidth: 0,
          }}
        >
          <Box
            component="img"
            src="/logo-nav.png"
            alt="Business Solutions"
            sx={{
              height: 44,
              width: 'auto',
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.92)',
              p: 0.5,
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.3,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Business Solutions
          </Typography>
        </Box>
        <Tooltip title={mode === 'dark' ? 'الوضع الفاتح' : 'الوضع الداكن'}>
          <IconButton color="inherit" onClick={toggleMode} sx={{ mr: 1, flexShrink: 0 }}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>

        {currentUser && (
          <>
            <IconButton color="inherit" onClick={openNotifMenu} sx={{ mr: 1, flexShrink: 0 }}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={notifAnchorEl}
              open={!!notifAnchorEl}
              onClose={closeNotifMenu}
              PaperProps={{ sx: { width: 340, maxHeight: 420 } }}
            >
              {notifications.length === 0 && <MenuItem disabled>مفيش إشعارات</MenuItem>}
              {notifications.map((n, index) => (
                <React.Fragment key={n.id}>
                  {index > 0 && <Divider />}
                  <MenuItem
                    onClick={() => handleNotifClick(n)}
                    sx={{ whiteSpace: 'normal', display: 'block', py: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {n.title}
                    </Typography>
                    {n.body && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {n.body}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {new Date(n.created_at).toLocaleString('ar-EG')}
                    </Typography>
                  </MenuItem>
                </React.Fragment>
              ))}
            </Menu>
          </>
        )}
        {currentUser ? (
          <>
            <Box
              onClick={() => navigate('/profile')}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2, cursor: 'pointer' }}
            >
              <Avatar src={currentUser.avatarUrl || undefined} sx={{ width: 32, height: 32 }}>
                {currentUser.name?.charAt(0)}
              </Avatar>
              <Typography sx={{ display: { xs: 'none', sm: 'block' }, whiteSpace: 'nowrap' }}>
                {currentUser.name} ({ROLE_LABELS[currentUser.roleSlug] || currentUser.roleName || '—'})
              </Typography>
            </Box>
            <Button color="inherit" onClick={handleLogout} sx={{ flexShrink: 0 }}>
              تسجيل الخروج
            </Button>
          </>
        ) : (
          <Button color="inherit" onClick={() => navigate('/')}>
            دخول
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
